import type { VoidFunctionComponent } from 'react';

import { useOnConfirm } from '../../../../../../../contexts/DialogContext';
import { useAddToast } from '../../../../../../../contexts/ToastContext';
import { useHasPermissionForTeamProjects } from '../../../../../../../helpers/permissions';
import { useDeleteTeamGroupTeam } from '../../../../../../../hooks/api';
import { Action } from '../../../../../../../shared/permissions';
import { icons } from '../../../../../../generic';
import SettingsTableRowActions from '../../../../../shared/SettingsTableRowActions';

type Props = {
  teamGroupId: number;
  teamId: number;
};

/**
 * Displays a remove button for a team group team.
 */
const TeamGroupTeamsTableRemove: VoidFunctionComponent<Props> = ({ teamGroupId, teamId }) => {
  const addToast = useAddToast();
  const onConfirm = useOnConfirm();
  const {
    mutateAsync: removeTeamFromTeamGroup,
    isLoading: isRemovingUser,
  } = useDeleteTeamGroupTeam(teamGroupId, teamId);

  // Reminder: we treat Teams in Team Groups as projects when it comes to RBAC checks.
  const hasPermissionForTeamProjects = useHasPermissionForTeamProjects();
  const canRemoveTeams = hasPermissionForTeamProjects(Action.Remove, teamGroupId);

  const handleRemoveUser = () => {
    onConfirm({
      title: 'Are you sure?',
      body: 'Are you sure you want to remove this team from this team group?',
      confirmText: 'Remove',
      onConfirm: async () => {
        try {
          await removeTeamFromTeamGroup();
          addToast({
            type: 'success',
            content: 'Team removed from team group',
          });
        } catch (error) {
          addToast({
            type: 'error',
            content: 'Error removing team from team group',
          });
        }
      },
    });
  };

  return (
    <SettingsTableRowActions>
      <SettingsTableRowActions.IconButton
        tooltipId={`remove-team-group-team-btn-${teamGroupId}`}
        tooltip={!canRemoveTeams ? 'You do not have permission to remove teams from this team group.' : 'Remove Team'}
        icon={icons.trashRegular}
        isDisabled={isRemovingUser || !canRemoveTeams}
        onClick={handleRemoveUser}
      />
    </SettingsTableRowActions>
  );
};

export default TeamGroupTeamsTableRemove;
